import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CARRIER_DISPLAY_NAMES,
  carrierPortalUrl,
  insuranceBayCarriersFor,
  type CarrierRouteMap,
} from "@aam/shared";
import { getSessionToken } from "../../hooks/useSession.js";
import { cn } from "../../lib/cn.js";
import { requestSurelcLogin, submitSurelcForm } from "../../lib/surelcSso.js";
import {
  Breadcrumbs,
  Button,
  Card,
  CardDescription,
  CardTitle,
  LoadingStatus,
  Skeleton,
  SkeletonText,
  StatusBadge,
} from "../ui/index.js";
import { ContractingOnboardingGuide } from "./ContractingOnboardingGuide.js";
import { InsuranceBayCard } from "./InsuranceBayCard.js";
import { PortalPageIntro, PortalWatermarkIcon } from "./PortalVisuals.js";
import { SetupStepHeader } from "./SetupStepHeader.js";
import {
  SurelcOnboardingCard,
  type SurelcOnboardingSummary,
} from "./SurelcOnboardingCard.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface PortalContract {
  id: string;
  carrier: string;
  imo: string;
  status: string;
  writingNumber: string | null;
  agentWritingNumber?: string | null;
  upstreamStatusName?: string | null;
  statusUpdatedAt: string;
}

function formatCarrier(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ContractsSkeleton() {
  return (
    <section className="space-y-8" role="status" aria-busy="true">
      <LoadingStatus label="Loading your contracts" />
      <div className="space-y-3 border-b-2 border-border-default pb-7">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-12 w-64 max-w-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-24 w-full rounded-card" />
        <Skeleton className="h-24 w-full rounded-card" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} padding="lg" className="min-h-[260px] space-y-5 border-2">
            <div className="flex items-start justify-between">
              <SkeletonText width="w-28" />
              <Skeleton className="h-6 w-20 rounded-lg" />
            </div>
            <SkeletonText width="w-40" />
            <Skeleton className="h-10 w-full rounded-xl" />
          </Card>
        ))}
      </div>
    </section>
  );
}

interface PortalAgentSummary {
  summary: {
    contractCount: number;
    activeContractCount: number;
  };
  contracts: PortalContract[];
  /** Saved carrier routes from /dashboard/carriers; defaults fill the rest. */
  carrierRoutes?: CarrierRouteMap;
  organization?: { brandName?: string | null } | null;
}

export interface PortalContractsPageProps {
  /**
   * "page" is the standalone Contracts route; "step" is the same content
   * rendered as step 1 inside the Setup page, under the progress bar.
   */
  variant?: "page" | "step";
  complete?: boolean;
  /** Called after the agent clicks Proceed past their first SureLC request. */
  onProceeded?: () => void;
}

export function PortalContractsPage({
  variant = "page",
  complete,
  onProceeded,
}: PortalContractsPageProps = {}) {
  const token = getSessionToken();
  const [contracts, setContracts] = useState<PortalContract[] | null>(null);
  const [carrierRoutes, setCarrierRoutes] = useState<CarrierRouteMap>({});
  const [organizationName, setOrganizationName] = useState("Altura");
  const [error, setError] = useState<string | null>(null);
  const [surelcOnboarding, setSurelcOnboarding] = useState<SurelcOnboardingSummary | null>(null);
  const [openingSurelc, setOpeningSurelc] = useState(false);
  const [surelcError, setSurelcError] = useState<string | null>(null);
  const [proceeding, setProceeding] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    async function loadContracts() {
      try {
        setError(null);
        const response = await fetch(`${API_BASE}/api/agents/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || "Unable to load contracts");
        }
        if (!cancelled) {
          const data = json.data as PortalAgentSummary;
          setContracts(data.contracts);
          setCarrierRoutes(data.carrierRoutes ?? {});
          if (data.organization?.brandName) setOrganizationName(data.organization.brandName);
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load contracts");
      }
    }

    void loadContracts();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void fetch(`${API_BASE}/api/agents/me/surelc-onboarding`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json().catch(() => null);
        if (!cancelled && response.ok && json?.success && json.data) {
          setSurelcOnboarding(json.data as SurelcOnboardingSummary);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function openSurelc() {
    if (!token || openingSurelc) return;
    const popup = window.open("", "surelc-sso");
    if (!popup) {
      setSurelcError("Allow pop-ups for this site, then open SureLC again.");
      return;
    }
    setOpeningSurelc(true);
    setSurelcError(null);
    try {
      const login = await requestSurelcLogin(token);
      submitSurelcForm(popup, login);
    } catch (launchError) {
      popup.close();
      setSurelcError(launchError instanceof Error ? launchError.message : "Unable to open SureLC");
    } finally {
      setOpeningSurelc(false);
    }
  }

  async function proceedPastSurelc() {
    if (!token || proceeding) return;
    setProceeding(true);
    setSurelcError(null);
    try {
      const response = await fetch(`${API_BASE}/api/agents/me/surelc-onboarding/proceed`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) {
        throw new Error(json?.error || "Unable to continue yet");
      }
      setSurelcOnboarding(json.data as SurelcOnboardingSummary);
      onProceeded?.();
    } catch (proceedError) {
      setSurelcError(proceedError instanceof Error ? proceedError.message : "Unable to continue yet");
    } finally {
      setProceeding(false);
    }
  }

  const summary = useMemo(
    () =>
      (contracts ?? []).reduce(
        (counts, contract) => {
          if (contract.status === "ACTIVE") counts.active += 1;
          if (contract.status === "PENDING") counts.pending += 1;
          return counts;
        },
        { active: 0, pending: 0 }
      ),
    [contracts]
  );

  const insuranceBayCarriers = useMemo(
    () => insuranceBayCarriersFor(contracts ?? [], carrierRoutes),
    [contracts, carrierRoutes]
  );
  // Carriers finished in the organization's own SureLC come first. Other
  // guides that would compete with it (Reagan) wait until SureLC is done.
  const showInsuranceBay = Boolean(surelcOnboarding) && insuranceBayCarriers.length > 0;
  const surelcDone = Boolean(surelcOnboarding?.proceededAt || surelcOnboarding?.canProceed);

  if (contracts === null && !error) return <ContractsSkeleton />;

  const startContractingLink = (
    <Link
      to="/my-agent/contracts/apply"
      className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-accent-strong bg-accent-strong px-6 py-3 text-sm font-bold text-on-accent hover:brightness-95"
    >
      Start contracting <span aria-hidden>→</span>
    </Link>
  );

  return (
    <section className="space-y-8" aria-labelledby={variant === "step" ? undefined : "contracts-title"}>
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="contents"
      >
        {variant === "step" ? (
          <SetupStepHeader
            stepId="contracting"
            complete={complete}
            action={contracts?.length ? startContractingLink : undefined}
          />
        ) : (
          <PortalPageIntro
            titleId="contracts-title"
            title="Contracts"
            breadcrumbs={
              <Breadcrumbs
                items={[
                  { label: "Setup", to: "/my-agent" },
                  { label: "Contracts", current: true },
                ]}
              />
            }
            action={contracts?.length ? startContractingLink : undefined}
          />
        )}
      </motion.header>

      {!error && contracts?.length === 0 ? (
        <Card padding="lg" className="flex flex-col gap-5 border-2 border-accent-strong sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <CardTitle>Start your first contract</CardTitle>
            <p className="mt-2 text-base font-extrabold text-content-primary">
              Select and submit at least one carrier request.
            </p>
            <CardDescription className="mt-1">
              Your services and downlines are available either way.
            </CardDescription>
          </div>
          {startContractingLink}
        </Card>
      ) : null}

      {showInsuranceBay && surelcOnboarding ? (
        <InsuranceBayCard
          organizationName={organizationName}
          carrierLabels={insuranceBayCarriers.map((carrier) => CARRIER_DISPLAY_NAMES[carrier])}
          canOpen={Boolean(surelcOnboarding.producerId && surelcOnboarding.ssoReady)}
          opening={openingSurelc}
          error={surelcError}
          onOpen={() => void openSurelc()}
        />
      ) : null}

      {surelcOnboarding ? (
        <SurelcOnboardingCard
          onboarding={surelcOnboarding}
          opening={openingSurelc}
          proceeding={proceeding}
          error={surelcError}
          onOpen={() => void openSurelc()}
          onProceed={() => void proceedPastSurelc()}
        />
      ) : null}

      {error ? (
        <Card padding="lg" className="border-status-rejected-border bg-status-rejected-bg">
          <CardTitle>Contracts are temporarily unavailable</CardTitle>
          <CardDescription className="mt-2">{error}</CardDescription>
          <Button className="mt-5" variant="secondary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </Card>
      ) : contracts?.length === 0 ? null : (
        <>
          <ContractingOnboardingGuide
            contracts={contracts ?? []}
            routes={carrierRoutes}
            hiddenSectionIds={showInsuranceBay && !surelcDone ? ["reagan"] : []}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Card padding="lg" className="relative flex min-h-24 items-center gap-4 overflow-hidden border-2 bg-status-active-bg border-status-active-border">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-surface-raised text-status-active-text" aria-hidden>
                ✓
              </span>
              <div>
                <p className="font-display text-3xl font-extrabold text-content-primary">{summary.active}</p>
                <p className="text-sm font-bold text-content-secondary">Active appointments</p>
              </div>
            </Card>
            <Card padding="lg" className="flex min-h-24 items-center gap-4 border-2 bg-status-pending-bg border-status-pending-border">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-surface-raised text-status-pending-text" aria-hidden>
                ◷
              </span>
              <div>
                <p className="font-display text-3xl font-extrabold text-content-primary">{summary.pending}</p>
                <p className="text-sm font-bold text-content-secondary">Appointments in progress</p>
              </div>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {contracts?.map((contract, index) => {
              const effectiveWritingNumber = contract.agentWritingNumber ?? contract.writingNumber;
              const carrierName = formatCarrier(contract.carrier);
              // Only an active appointment gets you into the carrier's portal,
              // and only carriers we have a quick link on file for.
              const portalUrl =
                contract.status === "ACTIVE" ? carrierPortalUrl(contract.carrier) : null;
              const wrapCard = Boolean(portalUrl);
              const card = (
                <Card
                  padding="lg"
                  className={cn(
                    "relative flex min-h-[260px] h-full flex-col gap-5 overflow-hidden border-2",
                    wrapCard && "transition-colors group-hover:border-accent-strong"
                  )}
                >
                  <div className="pointer-events-none absolute -bottom-8 -right-6 h-32 w-32 text-accent opacity-[0.07]" aria-hidden>
                    <PortalWatermarkIcon kind="contracts" />
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-lg font-semibold text-content-primary">{carrierName}</p>
                    </div>
                    <StatusBadge status={contract.status} size="sm" />
                  </div>
                  <div className="rounded-xl bg-surface-overlay p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-content-muted">Writing number</p>
                    <p className="mt-1 font-mono text-sm font-medium text-content-primary">
                      {effectiveWritingNumber ?? "Not assigned yet"}
                    </p>
                  </div>
                  <p className="mt-auto text-xs leading-5 text-content-secondary">
                    {contract.upstreamStatusName
                      ? `Latest carrier update: ${contract.upstreamStatusName}`
                      : "We will show carrier updates here as they arrive."}
                  </p>
                  {wrapCard && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-accent">
                      Open carrier portal <span aria-hidden>↗</span>
                    </span>
                  )}
                </Card>
              );
              return (
                <motion.article
                  key={contract.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.15) }}
                  className="h-full"
                >
                  {wrapCard && portalUrl ? (
                    <a
                      href={portalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="focus-ring group block h-full rounded-[var(--card-radius)]"
                      aria-label={`Open the ${carrierName} agent portal (opens in a new tab)`}
                    >
                      {card}
                    </a>
                  ) : (
                    card
                  )}
                </motion.article>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
